import type { WholeOrderDecisionResponseType } from "@sellar-io/api-v2/types";
import { Button } from "@sellar-io/components";
import { useState } from "react";
import { BasicDialog } from "../../../components/modal/BasicDialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useWholeOrderExceptionDecision } from "../../../hooks/api/v2/order/useWholeOrderExceptionDecision";
import { historyDate } from "../deliveryHistory/deliveryHistoryDates";

type Decision = "waitForOriginalOrder" | "cancellationRequested";

function canCancelOrder(resolution: WholeOrderDecisionResponseType) {
  switch (resolution.decisionState) {
    case "pendingBuyerDecision":
    case "waitForOriginalOrder":
      return true;
    case "cancellationRequested":
      return resolution.isCollectionFailure;
    default:
      return false;
  }
}

function getDecisionMessage(resolution: WholeOrderDecisionResponseType) {
  if (resolution.decisionState === "waitForOriginalOrder") {
    return "You have chosen to wait. Sellar will arrange a new collection and delivery.";
  }
  if (resolution.decisionState === "cancellationRequested") {
    return "Your cancellation has not completed yet. If it remains pending, try cancelling again or contact Sellar.";
  }
  if (resolution.isCollectionFailure) {
    return `When our driver arrived, ${resolution.supplierName} was unable to provide the stock for your order. You can either cancel your order or choose to wait for another collection attempt.`;
  }
  return "Your order is unavailable. You can choose to wait, or request cancellation if you no longer wish to wait.";
}

export function WholeOrderExceptionBuyerActionCard({
  orderId,
  onViewDetails,
}: {
  orderId: number;
  onViewDetails: () => void;
}) {
  const { query, estimateQuery, mutation, enabled } =
    useWholeOrderExceptionDecision(orderId);
  const resolution = query.data;
  const [pendingDecision, setPendingDecision] = useState<Decision | null>(null);
  if (!enabled) {
    return null;
  }
  if (
    (query.isSuccess && !resolution) ||
    (resolution &&
      (resolution.status !== "pending" || !canCancelOrder(resolution)))
  ) {
    return (
      <div className="rounded border border-blue-200 bg-blue-50 p-3">
        <p className="mb-2 text-sm font-semibold">
          Important info about delivery
        </p>
        <Button size="small" color="secondary" onClick={onViewDetails}>
          View details
        </Button>
      </div>
    );
  }
  const proposedDeliveryAt =
    resolution?.scheduledDeliveryAt ??
    resolution?.acceptedEstimate?.deliveryAt ??
    estimateQuery.data?.deliveryAt;
  const deliveryEstimate = proposedDeliveryAt
    ? `Your updated, expected delivery date will be ${historyDate(new Date(proposedDeliveryAt))}.`
    : "Your updated delivery date is to be confirmed.";
  const estimateText =
    estimateQuery.isPending &&
    resolution?.decisionState === "pendingBuyerDecision"
      ? "Calculating your next estimated delivery date…"
      : deliveryEstimate;
  const errorMessage =
    apiV2AssertError(mutation.error)?.response?.data.message ??
    "We couldn’t save your decision. Please refresh or contact Sellar.";
  const submitDecisionOrConfirmCollectionFailure = (decision: Decision) => {
    if (resolution?.isCollectionFailure) {
      setPendingDecision(decision);
      return;
    }
    mutation.mutate(decision);
  };
  return (
    <div className="space-y-3 rounded-lg border border-orange-200 bg-orange-50 p-3">
      <h3 className="text-sm font-semibold">
        Delivery delayed — decide what happens next
      </h3>
      {query.isPending ? (
        <p role="status" className="text-sm">
          Checking available options…
        </p>
      ) : query.isError || !resolution ? (
        <div role="alert" className="space-y-2">
          <p className="text-sm">
            We couldn’t load the available options. Please try again.
          </p>
          <Button
            size="small"
            disabled={query.isFetching}
            onClick={() => query.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : (
        <>
          <p className="text-sm">{getDecisionMessage(resolution)}</p>
          {resolution.decisionState === "pendingBuyerDecision" && (
            <p role="status" className="text-sm">
              {estimateText}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {resolution.decisionState === "pendingBuyerDecision" && (
              <Button
                size="small"
                disabled={
                  mutation.isPending ||
                  estimateQuery.isPending ||
                  resolution.cancellationInProgress
                }
                loading={
                  mutation.isPending &&
                  mutation.variables === "waitForOriginalOrder"
                }
                onClick={() =>
                  submitDecisionOrConfirmCollectionFailure(
                    "waitForOriginalOrder",
                  )
                }
              >
                Wait for my order
              </Button>
            )}
            <Button
              size="small"
              color="secondary"
              disabled={mutation.isPending || resolution.cancellationInProgress}
              loading={
                mutation.isPending &&
                mutation.variables === "cancellationRequested"
              }
              onClick={() =>
                submitDecisionOrConfirmCollectionFailure(
                  "cancellationRequested",
                )
              }
            >
              {resolution.isCollectionFailure
                ? "Cancel my order"
                : "Request cancellation"}
            </Button>
          </div>
        </>
      )}
      <Button size="small" color="secondary" onClick={onViewDetails}>
        View details
      </Button>
      {mutation.isError && (
        <p role="alert" className="text-sm text-red-600">
          {errorMessage}
        </p>
      )}
      {resolution && (
        <BasicDialog
          dialogActive={pendingDecision !== null}
          title={
            pendingDecision === "waitForOriginalOrder"
              ? "Confirm you wish to wait for your order"
              : "Confirm you wish to cancel your order"
          }
          loading={mutation.isPending}
          onDismiss={() => {
            if (!mutation.isPending) {
              setPendingDecision(null);
              mutation.reset();
            }
          }}
          onConfirm={() => {
            if (
              !pendingDecision ||
              mutation.isPending ||
              (pendingDecision === "waitForOriginalOrder" &&
                estimateQuery.isPending)
            ) {
              return;
            }
            mutation.mutate(pendingDecision, {
              onSuccess: () => setPendingDecision(null),
            });
          }}
        >
          <div className="flex flex-col gap-4 p-4 text-sm">
            <p>
              {pendingDecision === "waitForOriginalOrder"
                ? `The Sellar team will re-attempt collection from ${resolution.supplierName}. ${deliveryEstimate}`
                : `Your order will be cancelled immediately and ${resolution.supplierName} will be notified by email.`}
            </p>
            {mutation.isError && (
              <p role="alert" className="text-red-600">
                {errorMessage}
              </p>
            )}
          </div>
        </BasicDialog>
      )}
    </div>
  );
}
